import { Fragment, ReactNode } from 'react'
import { fromBase64 } from '@mysten/sui/utils'

import {
  AddCollateralInfo,
  AddLiquidityInfo,
  isAddCollateralInfo,
  isAddLiquidityInfo,
  isPositionCreationInfo,
  isRepayDebtInfo,
  PositionCreationInfo,
  RepayDebtInfo,
  isRebalanceInfo,
  RebalanceInfo,
  isReductionInfo,
  ReductionInfo,
  isDeleverageInfo,
  DeleverageInfo,
  isLiquidationInfo,
  LiquidationInfo,
  isDeletePositionInfo,
  isOwnerCollectFeeInfo,
  OwnerCollectFeeInfo,
  isOwnerCollectRewardInfo,
  OwnerCollectRewardInfo,
  isOwnerTakeStashedRewardsInfo,
  OwnerTakeStashedRewardsInfo,
} from '@/shared/gen/kai-leverage/position-core-clmm/structs'
import {
  CoinInfo,
  COIN_INFO_MAP,
  PositionConfigInfo,
  PhantomTypeArgument,
  TypeArgument,
  phantom,
  Amount,
  round,
} from '@kunalabs-io/kai'
import { Typography } from 'components/Typography/Typography'
import { DefaultTheme } from 'styled-components'
import { VecMap } from '@/shared/gen/sui/vec-map/structs'
import { TypeName } from '@/shared/gen/std/type-name/structs'
import { compressSuiType } from '@/shared/gen/_framework/util'
import { PositionInfo } from '@/shared/kai/hooks/useUserPositionDetails'
import { parseTypeName } from '@/shared/type'
import { GetActivityHistoryActivityType } from '@/shared/backend/types'
import { VaultDisplayInfo } from 'utils/vaultDisplayInfo'

export interface DisplayEvent {
  timestampMs: number
  action: string
  bcsBytes: string
  details: ReactNode
  transactionDigest: string
}

export const EventNames = {
  PositionCreationInfo: 'Create',
  AddCollateralInfo: 'Deposit',
  AddLiquidityInfo: 'Deposit',
  RepayDeptInfo: 'Deposit',
  RebalanceInfo: 'Compound',
  ReductionInfo: 'Withdraw',
  DeleverageInfo: 'Deleverage',
  LiquidationInfo: 'Liquidate',
  DeletePositionInfo: 'Close',
  OwnerCollectFeeInfo: 'Claim Fees',
  OwnerCollectRewardInfo: 'Claim Reward',
  OwnerTakeStashedRewardsInfo: 'Claim Stashed Reward',
  SavDeposit: 'Deposit',
  SavWithdraw: 'Withdraw',
  SavSend: 'Send',
  SavReceive: 'Receive',
}

const getImageAndSymbol = (imgSrc: string, symbol: string) => {
  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      <img
        src={imgSrc}
        alt={symbol}
        width={20}
        height={20}
        style={{ marginRight: 4, verticalAlign: 'middle', borderRadius: '100%' }}
      />
      {symbol}
    </span>
  )
}

interface ReverseEventWrapperProps {
  childX: ReactNode
  operator: ReactNode
  childY: ReactNode
  isReversed: boolean
}

export const ReverseEventWrapper = ({ childX, operator, childY, isReversed }: ReverseEventWrapperProps) => {
  return (
    <>
      {isReversed ? (
        <>
          {childY}
          {operator}
          {childX}
        </>
      ) : (
        <>
          {childX}
          {operator}
          {childY}
        </>
      )}
    </>
  )
}

const getDisplayMetricValue = (value: bigint, coinInfo: CoinInfo<string>) => {
  return coinInfo.newAmount(value).toString()
}

const getDeleveragedPctString = (deleverageInfo: DeleverageInfo) => {
  const l = deleverageInfo.model.l
  const deltaL = deleverageInfo.deltaL

  if (l === 0n) {
    return '0%'
  }
  const ratio = Number(deltaL) / Number(l)
  const pct = round(ratio * 100, 2)
  return `${pct}%`
}

const getCollectedAmmRewardsForDisplay = (collectedAmmRewards: VecMap<TypeName, 'u64'>) => {
  const { contents } = collectedAmmRewards
  const nodes: ReactNode[] = []
  for (let i = 0; i < contents.length; i++) {
    const coinInfo = COIN_INFO_MAP.get(compressSuiType('0x' + contents[i].key))
    if (!coinInfo) {
      continue
    }

    const node = (
      <Fragment>
        {getImageAndSymbol(coinInfo.iconUrl || '', coinInfo.symbol)}&nbsp;
        {getDisplayMetricValue(contents[i].value, coinInfo)}
        {i < contents.length - 1 && <span>&nbsp;+&nbsp;</span>}
      </Fragment>
    )
    nodes.push(node)
  }
  return nodes
}

const getDisplayCollectedAmmFeeX = (
  xSrc: string,
  xSymbol: string,
  collectedAmmFeeX: bigint,
  coinInfo: CoinInfo<string>,
  isReversed: boolean
) => {
  if (collectedAmmFeeX === 0n) {
    return null
  }
  return (
    <>
      {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
      {getDisplayMetricValue(collectedAmmFeeX, coinInfo)}
      {isReversed ? <>&nbsp;+&nbsp;</> : null}
    </>
  )
}

const getDisplayCollectedAmmFeeY = (
  ySrc: string,
  ySymbol: string,
  collectedAmmFeeY: bigint,
  coinInfo: CoinInfo<string>,
  isReversed: boolean
) => {
  if (collectedAmmFeeY === 0n) {
    return null
  }
  return (
    <>
      {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
      {getDisplayMetricValue(collectedAmmFeeY, coinInfo)}
      {isReversed ? null : <>&nbsp;+&nbsp;</>}
    </>
  )
}

const getDisplayLiquidator = (
  liquidatorReward: bigint,
  liquidationFee: bigint,
  imgSrc: string,
  symbol: string,
  coinInfo: CoinInfo<string>
) => {
  if (liquidatorReward + liquidationFee === 0n) {
    return null
  }

  return (
    <>
      {getImageAndSymbol(imgSrc, symbol)}&nbsp;
      {getDisplayMetricValue(liquidatorReward + liquidationFee, coinInfo)}
    </>
  )
}

export function getDecodedEvents(
  configInfo: PositionConfigInfo<PhantomTypeArgument, PhantomTypeArgument, TypeArgument>,
  events: PositionInfo['events'][number][]
): DisplayEvent[] {
  const displayEvents: DisplayEvent[] = []
  const coinX = configInfo.X
  const coinY = configInfo.Y

  const xSrc = coinX.iconUrl || ''
  const xSymbol = coinX.displaySymbol || ''
  const ySrc = coinY.iconUrl || ''
  const ySymbol = coinY.displaySymbol || ''
  const isReversedPair = configInfo.isReversedPair

  for (const event of events) {
    const serializedEvent = fromBase64(event.bcsBytes)
    if (isPositionCreationInfo(event.eventType)) {
      const positionCreation = PositionCreationInfo.fromBcs(serializedEvent)
      const hideExtraCollateral = positionCreation.cx === 0n && positionCreation.cy === 0n
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.PositionCreationInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Created LP position:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}
                    &nbsp;
                    {getDisplayMetricValue(positionCreation.x0, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                    {getDisplayMetricValue(positionCreation.y0, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
            {hideExtraCollateral ? null : (
              <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
                <span style={{ fontWeight: 600 }}>Extra Collateral:</span>&nbsp;
                <ReverseEventWrapper
                  childX={
                    <>
                      {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                      {getDisplayMetricValue(positionCreation.cx, coinX)}
                    </>
                  }
                  childY={
                    <>
                      {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                      {getDisplayMetricValue(positionCreation.cy, coinY)}
                    </>
                  }
                  operator={<>&nbsp;+&nbsp;</>}
                  isReversed={isReversedPair}
                />
              </div>
            )}
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Collateral:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                    {getDisplayMetricValue(positionCreation.x0 - positionCreation.dx + positionCreation.cx, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                    {getDisplayMetricValue(positionCreation.y0 - positionCreation.dy + positionCreation.cy, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Debt:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                    {getDisplayMetricValue(positionCreation.dx, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                    {getDisplayMetricValue(positionCreation.dy, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
          </Typography>
        ),
      })
    }
    if (isAddCollateralInfo(event.eventType)) {
      const addCollateralInfo = AddCollateralInfo.fromBcs(serializedEvent)
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.AddCollateralInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Added collateral to LP position:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                    {getDisplayMetricValue(addCollateralInfo.amountX, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}
                    &nbsp;
                    {getDisplayMetricValue(addCollateralInfo.amountY, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
          </Typography>
        ),
      })
    }
    if (isAddLiquidityInfo(event.eventType)) {
      const addLiquidityInfo = AddLiquidityInfo.fromBcs(serializedEvent)
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.AddLiquidityInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption" style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>Added liquidity to LP position:</span>&nbsp;
            <ReverseEventWrapper
              childX={
                <>
                  {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                  {getDisplayMetricValue(addLiquidityInfo.deltaX, coinX)}
                </>
              }
              childY={
                <>
                  {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                  {getDisplayMetricValue(addLiquidityInfo.deltaY, coinY)}
                </>
              }
              operator={<>&nbsp;+&nbsp;</>}
              isReversed={isReversedPair}
            />
          </Typography>
        ),
      })
    }
    if (isRepayDebtInfo(event.eventType)) {
      const repayDebtInfo = RepayDebtInfo.fromBcs(serializedEvent)
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.RepayDeptInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption" style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>Repaid debt of LP position:</span>&nbsp;
            <ReverseEventWrapper
              childX={
                <>
                  {getImageAndSymbol(xSrc, xSymbol)}
                  &nbsp;
                  {getDisplayMetricValue(repayDebtInfo.xRepaid, coinX)}
                </>
              }
              childY={
                <>
                  {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                  {getDisplayMetricValue(repayDebtInfo.yRepaid, coinY)}
                </>
              }
              operator={<>&nbsp;+&nbsp;</>}
              isReversed={isReversedPair}
            />
          </Typography>
        ),
      })
    }
    if (isRebalanceInfo(event.eventType)) {
      const rebalanceInfo = RebalanceInfo.fromBcs(serializedEvent)
      const { collectedAmmRewards } = rebalanceInfo
      const hideCollectedAmmFee = rebalanceInfo.collectedAmmFeeX === 0n && rebalanceInfo.collectedAmmFeeY === 0n
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.RebalanceInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 4 }}>
              <span style={{ fontWeight: 600 }}>Compounded LP position rewards:</span>&nbsp;
              {hideCollectedAmmFee ? null : (
                <ReverseEventWrapper
                  childX={getDisplayCollectedAmmFeeX(
                    xSrc,
                    xSymbol,
                    rebalanceInfo.collectedAmmFeeX,
                    coinX,
                    isReversedPair
                  )}
                  childY={getDisplayCollectedAmmFeeY(
                    ySrc,
                    ySymbol,
                    rebalanceInfo.collectedAmmFeeY,
                    coinY,
                    isReversedPair
                  )}
                  operator={<>&nbsp;+&nbsp;</>}
                  isReversed={isReversedPair}
                />
              )}
              {getCollectedAmmRewardsForDisplay(collectedAmmRewards)}&nbsp;
            </div>
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 4 }}>
              <span style={{ fontWeight: 600 }}>LP increased by:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}
                    &nbsp;
                    {getDisplayMetricValue(rebalanceInfo.deltaX, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                    {getDisplayMetricValue(rebalanceInfo.deltaY, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
          </Typography>
        ),
      })
    }
    if (isReductionInfo(event.eventType)) {
      const reductionInfo = ReductionInfo.fromBcs(serializedEvent)
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.ReductionInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Withdrawn from LP position:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}
                    &nbsp;
                    {getDisplayMetricValue(reductionInfo.withdrawnX, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                    {getDisplayMetricValue(reductionInfo.withdrawnY, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Debt repaid:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                    {getDisplayMetricValue(reductionInfo.xRepaid, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                    {getDisplayMetricValue(reductionInfo.yRepaid, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
          </Typography>
        ),
      })
    }
    if (isDeleverageInfo(event.eventType)) {
      const develerageInfo = DeleverageInfo.fromBcs(serializedEvent)
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.DeleverageInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption" style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>
              Deleveraged {getDeleveragedPctString(develerageInfo)} of LP position:
            </span>
            &nbsp;
            <ReverseEventWrapper
              childX={
                <>
                  {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                  {getDisplayMetricValue(develerageInfo.deltaX, coinX)}
                </>
              }
              childY={
                <>
                  {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                  {getDisplayMetricValue(develerageInfo.deltaY, coinY)}
                </>
              }
              operator={<>&nbsp;+&nbsp;</>}
              isReversed={isReversedPair}
            />
          </Typography>
        ),
      })
    }
    if (isLiquidationInfo(event.eventType)) {
      const liquidationInfo = LiquidationInfo.fromBcs(serializedEvent)
      const hidePlus =
        liquidationInfo.liquidatorRewardX + liquidationInfo.liquidationFeeX === 0n ||
        liquidationInfo.liquidatorRewardY + liquidationInfo.liquidationFeeY === 0n
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.LiquidationInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Collateral liquidated:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getDisplayLiquidator(
                      liquidationInfo.liquidatorRewardX,
                      liquidationInfo.liquidationFeeX,
                      xSrc,
                      xSymbol,
                      coinX
                    )}
                  </>
                }
                childY={
                  <>
                    {getDisplayLiquidator(
                      liquidationInfo.liquidatorRewardY,
                      liquidationInfo.liquidationFeeY,
                      ySrc,
                      ySymbol,
                      coinY
                    )}
                  </>
                }
                operator={hidePlus ? null : <>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Debt repaid:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {liquidationInfo.xRepaid === 0n ? null : getImageAndSymbol(xSrc, xSymbol)}
                    &nbsp;
                    {liquidationInfo.xRepaid === 0n ? null : getDisplayMetricValue(liquidationInfo.xRepaid, coinX)}
                  </>
                }
                childY={
                  <>
                    {liquidationInfo.yRepaid === 0n ? null : (
                      <>
                        {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                        {getDisplayMetricValue(liquidationInfo.yRepaid, coinY)}
                      </>
                    )}
                  </>
                }
                operator={liquidationInfo.xRepaid === 0n || liquidationInfo.yRepaid === 0n ? null : <>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
          </Typography>
        ),
      })
    }
    if (isOwnerCollectRewardInfo(event.eventType)) {
      const parsedType = parseTypeName(event.eventType)
      const rewardCoinType = compressSuiType(parsedType.typeArgs[0])
      const ownerCollectRewardInfo = OwnerCollectRewardInfo.r(phantom(rewardCoinType)).fromBcs(serializedEvent)

      const rewardToken = COIN_INFO_MAP.get(rewardCoinType)
      if (!rewardToken) {
        continue
      }

      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.OwnerCollectRewardInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Claim Reward:</span>&nbsp;
              {getImageAndSymbol(rewardToken?.iconUrl || '', rewardToken?.displaySymbol || '')}&nbsp;
              {getDisplayMetricValue(ownerCollectRewardInfo.collectedRewardAmt, rewardToken)}
            </div>
          </Typography>
        ),
      })
    }
    if (isOwnerTakeStashedRewardsInfo(event.eventType)) {
      const parsedType = parseTypeName(event.eventType)
      const rewardCoinType = compressSuiType(parsedType.typeArgs[0])
      const ownerTakeStashedRewardsInfo = OwnerTakeStashedRewardsInfo.r(phantom(rewardCoinType)).fromBcs(
        serializedEvent
      )

      if (ownerTakeStashedRewardsInfo.amount === 0n) {
        continue
      }
      const rewardToken = COIN_INFO_MAP.get(rewardCoinType)
      if (!rewardToken) {
        continue
      }
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.OwnerTakeStashedRewardsInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Claim Stashed Reward:</span>&nbsp;
              {getImageAndSymbol(rewardToken?.iconUrl || '', rewardToken?.displaySymbol || '')}&nbsp;
              {getDisplayMetricValue(ownerTakeStashedRewardsInfo.amount, rewardToken)}
            </div>
          </Typography>
        ),
      })
    }
    if (isOwnerCollectFeeInfo(event.eventType)) {
      const ownerCollectFeeInfo = OwnerCollectFeeInfo.fromBcs(serializedEvent)
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.OwnerCollectFeeInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption">
            <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
              <span style={{ fontWeight: 600 }}>Claimed LP fees:</span>&nbsp;
              <ReverseEventWrapper
                childX={
                  <>
                    {getImageAndSymbol(xSrc, xSymbol)}&nbsp;
                    {getDisplayMetricValue(ownerCollectFeeInfo.collectedXAmt, coinX)}
                  </>
                }
                childY={
                  <>
                    {getImageAndSymbol(ySrc, ySymbol)}&nbsp;
                    {getDisplayMetricValue(ownerCollectFeeInfo.collectedYAmt, coinY)}
                  </>
                }
                operator={<>&nbsp;+&nbsp;</>}
                isReversed={isReversedPair}
              />
            </div>
          </Typography>
        ),
      })
    }
    if (isDeletePositionInfo(event.eventType)) {
      displayEvents.push({
        transactionDigest: event.transactionDigest,
        timestampMs: event.timestampMs,
        action: EventNames.DeletePositionInfo,
        bcsBytes: event.bcsBytes,
        details: (
          <Typography variant="caption" style={{ fontWeight: 600 }}>
            LP position closed.
          </Typography>
        ),
      })
    }
  }

  return displayEvents
}

export const getActivityActionTextColor = (activity: string, theme: DefaultTheme): string => {
  switch (activity) {
    case EventNames.AddCollateralInfo:
      return theme.colors.text.success
    case EventNames.ReductionInfo:
    case EventNames.LiquidationInfo:
      return theme.colors.text.danger
    case EventNames.DeleverageInfo:
      return theme.colors.text.danger2
    case EventNames.PositionCreationInfo:
    case EventNames.SavReceive:
      return theme.colors.text.create
    case EventNames.DeletePositionInfo:
      return '#fff'
    case EventNames.OwnerCollectFeeInfo:
    case EventNames.OwnerCollectRewardInfo:
    case EventNames.OwnerTakeStashedRewardsInfo:
      return theme.colors.text.success
    default:
      return ''
  }
}

export const getActivityActionBgColor = (activity: string, theme: DefaultTheme): string => {
  switch (activity) {
    case EventNames.AddCollateralInfo:
      return theme.colors.text.successLabelBg
    case EventNames.ReductionInfo:
    case EventNames.LiquidationInfo:
      return theme.colors.text.dangerLabelBg
    case EventNames.DeleverageInfo:
      return theme.colors.text.danger2LabelBg
    case EventNames.PositionCreationInfo:
    case EventNames.SavReceive:
      return theme.colors.text.createLabelBg
    case EventNames.DeletePositionInfo:
      return theme.colors.text.danger
    default:
      return ''
  }
}

export const getActivityBorderColor = (activity: string, theme: DefaultTheme): string => {
  switch (activity) {
    case EventNames.AddCollateralInfo:
      return theme.colors.text.success
    case EventNames.ReductionInfo:
    case EventNames.LiquidationInfo:
      return theme.colors.text.danger
    case EventNames.DeleverageInfo:
      return theme.colors.text.danger2
    case EventNames.PositionCreationInfo:
    case EventNames.SavReceive:
      return theme.colors.text.create
    case EventNames.DeletePositionInfo:
      return theme.colors.text.danger
    case EventNames.OwnerCollectFeeInfo:
    case EventNames.OwnerCollectRewardInfo:
    case EventNames.OwnerTakeStashedRewardsInfo:
      return theme.colors.background.success
    default:
      return ''
  }
}

export const getFiltersFromLocalStorage = (
  localStorageKey: string,
  filterKey: string,
  defaultFilters: Record<string, boolean>
): Record<string, boolean> => {
  const storedFilters = JSON.parse(localStorage.getItem(localStorageKey) || '{}')
  return {
    ...defaultFilters,
    ...(storedFilters[filterKey] || {}),
  }
}

export const saveFiltersToLocalStorage = (
  localStorageKey: string,
  filterKey: string,
  filters: Record<string, boolean>
) => {
  const storedFilters = JSON.parse(localStorage.getItem(localStorageKey) || '{}')
  storedFilters[filterKey] = filters
  localStorage.setItem(localStorageKey, JSON.stringify(storedFilters))
}

const getDepositWithdrawDetails = (title: string, vaultDisplayInfo: VaultDisplayInfo, tValue: string) => (
  <Typography variant="caption">
    <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
      <span style={{ fontWeight: 600, marginRight: 4 }}>{title}</span>&nbsp;
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <vaultDisplayInfo.icon width={20} height={20} style={{ marginRight: 4 }} />
        <div style={{ marginLeft: 4 }}>{vaultDisplayInfo.tSymbol}</div>
        <div style={{ marginLeft: 4 }}>
          {Amount.fromInt(BigInt(tValue), vaultDisplayInfo.vault.T.decimals).toString()}
        </div>
      </div>
    </div>
  </Typography>
)

const getSendReceiveDetails = (
  title: string,
  vaultDisplayInfo: VaultDisplayInfo,
  event: GetActivityHistoryActivityType
) => (
  <Typography variant="caption">
    <div style={{ padding: '2px 0', display: 'flex', alignItems: 'center' }}>
      <span style={{ fontWeight: 600 }}>{title}</span>&nbsp;
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ marginLeft: 4 }}>
          {Amount.fromInt(BigInt(event.ytAmount), vaultDisplayInfo.vault.YT.decimals).toString()}
        </div>
        <div style={{ marginLeft: 4 }}>{vaultDisplayInfo.ytSymbol}</div>
        <span style={{ display: 'flex', alignItems: 'center', marginLeft: 4 }}>
          (
          <div style={{ display: 'flex' }}>
            <vaultDisplayInfo.icon width={20} height={20} style={{ marginRight: 4 }} />
          </div>
          <div style={{ marginLeft: 4 }}>
            {Amount.fromInt(BigInt(event.tValue), vaultDisplayInfo.vault.T.decimals).toString()}
          </div>
          )
        </span>
      </div>
    </div>
  </Typography>
)

export const getSavEventDetails = (event: GetActivityHistoryActivityType, vaultDisplayInfo: VaultDisplayInfo) => {
  switch (event.kind) {
    case 'Deposit':
      return getDepositWithdrawDetails('Deposited amount:', vaultDisplayInfo, event.tValue)
    case 'Withdraw':
      return getDepositWithdrawDetails('Withdrawn amount:', vaultDisplayInfo, event.tValue)
    case 'Send':
      return getSendReceiveDetails('Sent amount:', vaultDisplayInfo, event)
    default:
      return getSendReceiveDetails('Received amount:', vaultDisplayInfo, event)
  }
}
